import { Divider, LinearProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import first from "lodash/first";
import { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";

import { AvailabilityListOptionListItemInput } from "@holibob-packages/graphql-types";
import { generateUUID } from "@holibob-packages/id";

import { ProductData } from "../../apiHooks";
import { useAvailabilityDatePaxListQuery } from "../../apiHooks/graphql";
import { Flex } from "../../components";
import { DateInput } from "../../components/DateInput";
import { NumberControl } from "../../components/NumberControl";
import { useNextTranslation } from "../../hooks";

type AvailabilityDatePaxModalHeaderProps = {
    product: ProductData;
    selectedAvailabilityId?: string | null;
    onChange(availabilityId: string | null, isDefault: boolean): void;
};

export function AvailabilityDatePaxModalHeader(props: AvailabilityDatePaxModalHeaderProps) {
    const { product, selectedAvailabilityId, onChange } = props;
    const [optionList, setOptionList] = useOptionListState(product.startPlace?.timeZone ?? "UTC");
    const [t] = useNextTranslation("availability");
    const [sessionId] = useState<string>(generateUUID());

    const { data, loading, called } = useAvailabilityDatePaxListQuery({
        variables: {
            productId: product.id,
            optionList: optionList.length ? optionList : undefined,
            sessionId,
        },
        skip: optionList.length === 0,
    });

    const availability = first(data?.availabilityList?.nodes);
    const availableAvailabilityId = useSelectedAvailabilityId(
        loading,
        called,
        selectedAvailabilityId,
        availability?.id
    );

    useEffect(() => {
        if (availableAvailabilityId !== undefined) {
            onChange(availableAvailabilityId, true);
        }
    }, [availableAvailabilityId, onChange]);

    function onOptionListChange(id: string, value: string) {
        const newOptionsList = optionList.map((option) => (option.id === id ? { ...option, value } : option));
        setOptionList(newOptionsList);
    }

    const isAvailabilityListEmpty = !data?.availabilityList?.nodes.length;
    const minDate = dayjs.tz(new Date(), product.startPlace?.timeZone ?? "UTC");

    function onDateChange(date?: dayjs.Dayjs | null) {
        const value = date ?? minDate;
        onOptionListChange(optionList[0].id, value.format("YYYY-MM-DD"));
    }

    function onPaxChange(count: number) {
        onOptionListChange(optionList[1].id, count.toString());
    }

    return (
        <Wrapper column justifyContent="space-between" gap={24} data-empty={isAvailabilityListEmpty}>
            <Flex column gap={24}>
                <DateInput
                    label={t("label.date")}
                    value={dayjs(optionList[0].value)}
                    minDate={minDate}
                    onChange={onDateChange}
                />

                <NumberOfPeople value={optionList[1].value ? +optionList[1].value : 0} onChange={onPaxChange} />
            </Flex>

            <Footer loading={loading} availabilityId={availability?.id} />
        </Wrapper>
    );
}

const Wrapper = styled(Flex)(({ theme }) => ({
    '&[data-empty="true"]': {
        minHeight: 220,
        paddingBottom: theme.spacing(2),
    },
}));

type NumberOfPeopleProps = {
    value: number;
    onChange(value: number): void;
};
function NumberOfPeople(props: NumberOfPeopleProps) {
    const { value, onChange } = props;
    const optionValue = value ? +value : 0;
    const [t] = useNextTranslation("availability");

    const [_value, setValue] = useState(optionValue);
    const [debouncedValue] = useDebounce(_value, 500);

    useEffect(() => {
        if (optionValue === debouncedValue) {
            return;
        }

        onChange(debouncedValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionValue, debouncedValue]);

    function onChangeHandler(newValue: number) {
        setValue(newValue);
    }

    return (
        <Flex justifyContent="space-between" alignItems="center">
            <Typography>{t("label.noOfPeople")}</Typography>
            <NumberControl value={_value} min={1} max={null} onChange={onChangeHandler} />
        </Flex>
    );
}

type FooterProps = {
    loading: boolean;
    availabilityId?: string | null;
};
function Footer(props: FooterProps) {
    const [t] = useNextTranslation("availability");
    const { loading, availabilityId } = props;
    if (loading) {
        return (
            <Flex column gap={8}>
                <LinearProgress />
                <Divider />
            </Flex>
        );
    }

    if (!availabilityId) {
        return (
            <Flex column gap={8}>
                <Typography>{t("message.noAvailability2")}</Typography>
                <Divider />
            </Flex>
        );
    }

    return null;
}

function useOptionListState(timeZone: string) {
    return useState<AvailabilityListOptionListItemInput[]>([
        {
            id: generateUUID(),
            value: dayjs.tz(new Date(), timeZone).format("YYYY-MM-DD"),
            type: "DATE",
        },
        {
            id: generateUUID(),
            value: "2",
            type: "PAX_COUNT",
        },
    ]);
}

function useSelectedAvailabilityId(
    loading: boolean,
    called: boolean,
    selectedAvailabilityId?: string | null,
    availableAvailabilityId?: string | null
) {
    if (!selectedAvailabilityId && availableAvailabilityId) {
        return availableAvailabilityId;
    }

    if (loading && selectedAvailabilityId) {
        return null;
    }

    if (selectedAvailabilityId && !availableAvailabilityId) {
        return null;
    }

    if (selectedAvailabilityId && selectedAvailabilityId !== availableAvailabilityId) {
        return availableAvailabilityId;
    }

    if (called && !selectedAvailabilityId && !availableAvailabilityId) {
        return null;
    }

    return undefined;
}
